// vendor
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink as RRNavLink, useLocation } from "react-router-dom";
import classnames from "classnames";
import { useSelector } from "react-redux";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
// reactstrap components
import { Collapse, NavbarBrand, NavItem, NavLink, Container } from "reactstrap";

// app
import { NavbarLogo, NavStyled, StyledNavbar } from "./styled";
import LogoGray from "../../assets/img/logo_no_text.png";
import { getToken } from "../../utils/tokenStorage";
import { ACCOUNTS_ENABLED } from "../../constants";
import { landingPage } from "../../constants/descriptions";
import Banner, { BannerLink } from "../Banner";
import DeadlineBanner, { resolveDeadline } from "../DeadlineBanner";
import { PropertyIcon } from "../AddressAutosuggest";

const PRIMARY_NAV_ID = "primary-navigation";

function MainNavbar({ fixedColor }) {
  const location = useLocation();
  // Account-aware entry point: signed-in customers see "Account", everyone
  // else sees "Sign in". Read per-render from localStorage so it reflects the
  // current session as the user navigates.
  const isLoggedIn = !!getToken();
  // The transparent-over-hero navbar variant uses near-white
  // text — designed for the homepage's dark hero.  On every
  // other surface (SEO landing pages, /verify, etc.) the hero
  // is a cream-on-navy palette and white text reads as
  // invisible.  Treat any non-root route as "fixed color" so
  // the slate text + cream pill always applies.
  const isHomepage = location && location.pathname === "/";
  const useFixed = fixedColor || !isHomepage;
  const [navbarColor, setNavbarColor] = React.useState(
    useFixed ? "" : "navbar-transparent",
  );
  const [navbarCollapse, setNavbarCollapse] = React.useState(false);
  const navbarNode = useRef();
  const { brokerReferred } = useSelector((state) => state.session);

  // Deadline season + per-session dismissal.  The banner is shown
  // until either the protest deadline lapses (date check) or the
  // user clicks the Dismiss × button.  Dismissal is sessionStorage-
  // scoped so a returning visit re-shows the banner.
  const DISMISS_KEY = "pxn_deadline_banner_dismissed";
  const [deadlineDismissed, setDeadlineDismissed] = useState(false);
  useEffect(() => {
    try {
      if (
        typeof window !== "undefined" &&
        window.sessionStorage &&
        window.sessionStorage.getItem(DISMISS_KEY)
      ) {
        setDeadlineDismissed(true);
      }
    } catch (_e) { /* ignore */ }
  }, []);
  const handleDeadlineDismiss = () => {
    setDeadlineDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (_e) { /* ignore */ }
  };
  const deadlineActive = (() => {
    try {
      return (
        !deadlineDismissed && resolveDeadline().getTime() > Date.now()
      );
    } catch (_e) {
      return false;
    }
  })();
  const bannerOpen = !!brokerReferred || deadlineActive;

  // The fixed navbar reads ``--banner-stack-height`` for its
  // margin-top, so any banner showing above it (deadline, broker)
  // shifts the navbar by exactly the banner stack's actual rendered
  // height.  Tracked via ResizeObserver because the deadline-banner
  // copy wraps to 1, 2, or 3 lines depending on viewport width.
  const bannerStackRef = useRef(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = bannerStackRef.current;
    if (!bannerOpen || !node) {
      root.style.setProperty("--banner-stack-height", "0px");
      return undefined;
    }
    const update = () => {
      // offsetHeight rounds to integer pixels; that's all the
      // navbar margin needs.
      const h = node.offsetHeight || 0;
      root.style.setProperty("--banner-stack-height", `${h}px`);
    };
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(node);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.setProperty("--banner-stack-height", "0px");
    };
  }, [bannerOpen, deadlineActive, brokerReferred]);

  // Click-handler kept narrow on purpose: setState batches with React,
  // and the document-level classList flip happens in the useEffect
  // below — out of the input-to-paint critical path.  Doing the
  // classList.toggle inline here was the dominant contributor to the
  // navbar's INP on mobile (each toggle triggered a global style
  // recalc inside the same task as the React re-render).
  const toggleNavbarCollapse = () => {
    setNavbarCollapse((prev) => !prev);
  };

  // Sync ``html.nav-open`` with React state.  Runs in the commit
  // phase, after React has paint-committed the menu state — so the
  // user sees the menu animate without the global recalc holding up
  // their tap-to-paint frame.
  React.useEffect(() => {
    const cls = "nav-open";
    if (navbarCollapse) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => {
      document.documentElement.classList.remove(cls);
    };
  }, [navbarCollapse]);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (navbarNode.current.contains(e.target) || !navbarCollapse) {
        return;
      }

      toggleNavbarCollapse();
    };
    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [navbarCollapse]);

  React.useEffect(() => {
    // Instant jump to top on route change. A smooth-scroll on mount caused
    // the navbar to transition transparent↔solid mid-animation (flicker)
    // because scrollTop crossed the 100px threshold while decreasing.
    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // On routes where we force the fixed-color state (every
    // non-homepage), skip the scroll listener entirely.  The
    // earlier bug: this effect ran regardless of ``useFixed``
    // and re-applied ``navbar-transparent`` whenever the user
    // was scrolled to the top — so SEO pages still rendered
    // with the white-text variant against their cream hero.
    if (useFixed) {
      setNavbarColor("");
      return undefined;
    }
    // Throttle to once-per-frame via rAF and mark the listener
    // ``passive: true`` so the scroll thread isn't blocked.
    // Without these, scroll fires 60-300x/sec and each call
    // reads scrollTop (a layout-dependent property) right after
    // React's previous setState wrote a class to the navbar —
    // the classic forced-reflow R→W→R pattern.  PSI flagged the
    // entry-bundle scroll handler as the top forced-layout
    // contributor on /pricing.
    //
    // ``window.scrollY`` is identical to documentElement.scrollTop
    // on every relevant browser and reads more cleanly.
    let ticking = false;
    const updateNavbarColor = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const top = window.scrollY;
        setNavbarColor(top > 99 ? "" : "navbar-transparent");
      });
    };

    // Set initial state from actual scroll position so the first paint is correct
    updateNavbarColor();

    window.addEventListener("scroll", updateNavbarColor, { passive: true });

    return function cleanup() {
      window.removeEventListener("scroll", updateNavbarColor);
    };
  }, [useFixed]);

  return (
    <>
      {/* Banner stack — pinned to the viewport top so it stays
          visible while scrolling.  Wrapped in a single sticky
          container so we can ResizeObserve a single node and feed
          the sum-of-banner-heights to the navbar's margin-top.
          Inner banners render in normal flow inside this wrapper
          so each one contributes to the wrapper's offsetHeight. */}
      <div
        ref={bannerStackRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          width: "100%",
        }}
      >
        {deadlineActive && (
          <DeadlineBanner onDismiss={handleDeadlineDismiss} />
        )}
        {brokerReferred && brokerReferred.name && (
          <Banner>
            <p>
              Get your Free Assessment and discounted report thanks to <b>{startCase(toLower(brokerReferred.name))}'s</b>{" "}
              participation in our{" "}
              <BannerLink target="_blank" to="/terms-and-conditions#referral-program">
                {" "}
                Broker Referral Program!
              </BannerLink>
            </p>
          </Banner>
        )}
      </div>
      <div ref={navbarNode}>
        <StyledNavbar
          className={classnames("fixed-top", useFixed ? "" : navbarColor)}
          color-on-scroll="300"
          expand="lg"
          $bannerOpen={bannerOpen}
        >
          <Container>
            <div className="navbar-translate">
              <NavbarBrand data-placement="bottom" to="/" title="Protesting Property Taxes" tag={RRNavLink}>
                <NavbarLogo src={LogoGray} alt="Protesting Property Taxes logo" />
                <span className="navbar-brand-text">Protesting Property Taxes</span>
              </NavbarBrand>
              <button
                type="button"
                aria-label={navbarCollapse
                  ? "Close navigation menu"
                  : "Open navigation menu"}
                aria-controls={PRIMARY_NAV_ID}
                aria-expanded={navbarCollapse}
                className={classnames("navbar-toggler navbar-toggler", {
                  toggled: navbarCollapse,
                })}
                onClick={toggleNavbarCollapse}
              >
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </button>
            </div>
            <Collapse id={PRIMARY_NAV_ID} className="justify-content-end"
              navbar isOpen={navbarCollapse}>
              <NavStyled navbar>
                <NavItem>
                  <NavLink to="/" tag={RRNavLink} activeClassName="active">
                    <span aria-hidden="true" className="home-icon">
                      <PropertyIcon size={16} />
                    </span>
                    Home
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/pricing" tag={RRNavLink} activeClassName="active">
                    Pricing
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/faq" tag={RRNavLink} activeClassName="active">
                    FAQ
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/how-to-protest-property-taxes" tag={RRNavLink} activeClassName="active">
                    Learn
                  </NavLink>
                </NavItem>
                {ACCOUNTS_ENABLED && (
                  <NavItem>
                    <NavLink
                      to={isLoggedIn ? "/dashboard" : "/login"}
                      tag={RRNavLink}
                      activeClassName="active"
                    >
                      {isLoggedIn ? "Account" : "Sign in"}
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink to="/verify" tag={RRNavLink} activeClassName="active">
                    {landingPage.navbar.getASsessmentButtonText()}
                  </NavLink>
                </NavItem>
              </NavStyled>
            </Collapse>
          </Container>
        </StyledNavbar>
      </div>
    </>
  );
}

export default MainNavbar;
